<template>
  <el-dialog
    :model-value="visible"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="false"
    width="900px"
    class="login-modal-dialog"
    destroy-on-close
    @close="handleClose"
  >
    <div class="login-modal">
      <!-- 左侧品牌营销区 -->
      <div class="modal-brand">
        <!-- 装饰光晕 -->
        <div class="decor-circle decor-top-right"></div>
        <div class="decor-circle decor-bottom-left"></div>
        <div class="decor-circle decor-center"></div>

        <div class="brand-content">
          <!-- Logo -->
          <div class="brand-header">
            <img src="@/assets/imgs/logo.png" alt="logo" class="brand-logo-img" />
            <div>
              <h3 class="brand-title">紫金山AI</h3>
              <p class="brand-subtitle">ZIJIN MOUNTAIN CLOUD</p>
            </div>
          </div>

          <!-- 标题 -->
          <h2 class="brand-heading">
            <template v-if="currentState === LoginStateEnum.REGISTER"
              >开启企业<br />数字化之旅</template
            >
            <template v-else-if="currentState === LoginStateEnum.RESET_PASSWORD"
              >找回您的<br />账号密码</template
            >
            <template v-else>企业级<br />数字化平台</template>
          </h2>

          <p class="brand-desc">
            <template v-if="currentState === LoginStateEnum.REGISTER">
              注册即可免费试用 100+ 款产品，<br />3 分钟极速配置，零门槛上手。
            </template>
            <template v-else-if="currentState === LoginStateEnum.RESET_PASSWORD">
              通过手机验证码快速重置密码，<br />如有问题请联系客服 400-646-5288。
            </template>
            <template v-else>
              懂业务、能工作、会进化的智能数字化平台，<br />助力企业迈入智能体时代。
            </template>
          </p>

          <!-- 优势列表 -->
          <div class="brand-features">
            <div class="feature-item">
              <div class="feature-icon">
                <Icon icon="ep:check" :size="13" color="#fff" />
              </div>
              <span>100+ 款即买即用的数字化产品</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <Icon icon="ep:check" :size="13" color="#fff" />
              </div>
              <span>3 分钟极速配置上线</span>
            </div>
            <div class="feature-item">
              <div class="feature-icon">
                <Icon icon="ep:check" :size="13" color="#fff" />
              </div>
              <span>7×24 小时专家服务</span>
            </div>
          </div>
        </div>

        <!-- 底部统计 -->
        <div class="brand-footer">
          <p>已有 <strong>50,000+</strong> 企业选择紫金山AI</p>
        </div>
      </div>

      <!-- 右侧表单区 -->
      <div class="modal-form">
        <!-- 表单标题栏 -->
        <div class="form-header">
          <div class="form-tabs" v-if="showTabs">
            <button
              :class="['tab-btn', { active: tabMode === 'account' }]"
              @click="switchTab('account')"
            >
              账号登录
            </button>
            <button
              :class="['tab-btn', { active: tabMode === 'qrcode' }]"
              @click="switchTab('qrcode')"
            >
              扫码登录
            </button>
          </div>
          <h3 class="form-title" v-else>
            {{ currentState === LoginStateEnum.REGISTER ? '创建账号' : '找回密码' }}
          </h3>
          <button class="close-btn" @click="handleClose">
            <Icon icon="ep:close" :size="22" color="#9ca3af" />
          </button>
        </div>

        <!-- 表单区域：直接嵌入所有表单组件，显示/隐藏由 LoginStateEnum 控制 -->
        <div class="form-body">
          <!-- 账号登录 -->
          <LoginForm />
          <!-- 手机登录 -->
          <MobileForm />
          <!-- 二维码登录 -->
          <QrCodeForm />
          <!-- 忘记密码 -->
          <ForgetPasswordForm />
          <!-- 注册 -->
          <RegisterForm />
          <!-- SSO 授权 -->
          <SSOLoginVue />

          <!-- 底部切换链接 -->
          <div class="form-footer-link" v-if="currentState === LoginStateEnum.LOGIN">
            <p>
              还没有账号？
              <a class="link" @click="switchToRegister">立即注册</a>
            </p>
          </div>
          <div class="form-footer-link" v-else-if="currentState === LoginStateEnum.REGISTER">
            <p>
              已有账号？
              <a class="link" @click="switchToLogin">立即登录</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts" setup>
import { Icon } from '@/components/Icon'
import { usePortalState } from './usePortalState'
import { LoginStateEnum, useLoginState } from './components/useLogin'
import {
  LoginForm,
  MobileForm,
  QrCodeForm,
  RegisterForm,
  ForgetPasswordForm,
  SSOLoginVue
} from './components'

defineOptions({ name: 'LoginModal' })

const {
  isLoginModal,
  isRegisterModal,
  isForgetPasswordModal,
  closeModal,
  switchToLogin,
  switchToRegister,
  getCurrentModal
} = usePortalState()
const { getLoginState, setLoginState } = useLoginState()

const visible = computed(() => {
  const modal = getCurrentModal.value
  return modal === 'LOGIN' || modal === 'REGISTER' || modal === 'FORGET_PASSWORD'
})

const currentState = computed(() => getLoginState.value)

const tabMode = ref<'account' | 'qrcode'>('account')

// 是否显示 Tab 切换（仅在登录状态）
const showTabs = computed(() => currentState.value === LoginStateEnum.LOGIN)

function switchTab(mode: 'account' | 'qrcode') {
  tabMode.value = mode
  if (mode === 'qrcode') {
    setLoginState(LoginStateEnum.QR_CODE)
  } else {
    setLoginState(LoginStateEnum.LOGIN)
  }
}

function handleClose() {
  closeModal()
  // 关闭时重置为登录状态
  setLoginState(LoginStateEnum.LOGIN)
}

// 监听弹窗类型变化，同步切换表单状态
watch(
  () => getCurrentModal.value,
  (modal) => {
    if (modal === 'LOGIN' || modal === 'FORGET_PASSWORD') {
      // 登录弹窗：根据具体类型设置
      if (modal === 'FORGET_PASSWORD') {
        setLoginState(LoginStateEnum.RESET_PASSWORD)
      } else {
        setLoginState(LoginStateEnum.LOGIN)
      }
      tabMode.value = 'account'
    } else if (modal === 'REGISTER') {
      setLoginState(LoginStateEnum.REGISTER)
      tabMode.value = 'account'
    }
  },
  { immediate: true }
)
</script>

<style lang="scss">
/* 全局覆盖 el-dialog 样式（dialog teleport 到 body，scoped 不生效） */
.login-modal-dialog.el-dialog {
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 16px !important;
  overflow: hidden !important;
}

.login-modal-dialog .el-dialog__header {
  display: none !important;
}

.login-modal-dialog .el-dialog__body {
  padding: 0 !important;
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.login-modal-dialog {
  :deep(.el-dialog__body) {
    padding: 0;
    overflow: hidden;
  }

  :deep(.el-dialog__header) {
    display: none;
  }
}

.login-modal {
  display: flex;
  overflow: hidden;
  border-radius: 16px;
  max-height: 90vh;
}

// ===== 左侧品牌区 =====
.modal-brand {
  width: 460px;
  min-height: 540px;
  background: linear-gradient(135deg, #001b5e 0%, #0066ff 50%, #0044cc 100%);
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #fff;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.decor-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.decor-top-right {
  top: -80px;
  right: -80px;
  width: 280px;
  height: 280px;
}

.decor-bottom-left {
  bottom: -60px;
  left: -60px;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.05);
}

.decor-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 360px;
  height: 360px;
  background: rgba(255, 255, 255, 0.03);
}

.brand-content {
  position: relative;
  z-index: 2;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
}

.brand-logo-img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  background: #fff;
  border-radius: 12px;
  padding: 4px;
}

.brand-title {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
}

.brand-subtitle {
  font-size: 11px;
  opacity: 0.8;
  letter-spacing: 1px;
}

.brand-heading {
  font-size: 34px;
  font-weight: 700;
  margin-bottom: 18px;
  line-height: 1.25;
}

.brand-desc {
  font-size: 14px;
  opacity: 0.85;
  line-height: 1.8;
  margin-bottom: 48px;
}

.brand-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  opacity: 0.9;
}

.feature-icon {
  width: 22px;
  height: 22px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brand-footer {
  position: relative;
  z-index: 2;

  p {
    font-size: 12px;
    opacity: 0.7;

    strong {
      font-weight: 700;
    }
  }
}

// ===== 右侧表单区 =====
.modal-form {
  flex: 1;
  padding: 40px 36px 32px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #fff;
  overflow-y: auto;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.form-tabs {
  display: flex;
  gap: 24px;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 8px;
}

.form-title {
  font-size: 20px;
  font-weight: 700;
}

.tab-btn {
  font-size: 16px;
  font-weight: 700;
  color: #6b7280;
  padding: 0 0 10px;
  background: none;
  border: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;

  &.active {
    color: #0066ff;
    border-bottom-color: #0066ff;
  }

  &:hover:not(.active) {
    color: #374151;
  }
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 4px;
  margin-top: -4px;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }
}

.form-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-footer-link {
  text-align: center;
  margin-top: 16px;

  p {
    font-size: 13px;
    color: #6b7280;

    .link {
      color: #0066ff;
      cursor: pointer;
      font-weight: 600;
      transition: color 0.2s;

      &:hover {
        color: #0044cc;
      }
    }
  }
}

// 响应式
@media (max-width: 768px) {
  .modal-brand {
    display: none;
  }

  .modal-form {
    padding: 28px 20px 24px;
  }
}
</style>
